import { apiClient } from '@/shared/api';
import type {
  AdcodeRequestComment,
  OutreachFormWatchView,
  AdcodeRequestRecord,
  CreateDropSignalRequest,
  CreateOperationalIssueReportRequest,
  CreateScenarioRequest,
  Quest,
  Scenario,
  ScenarioOutreachFormStatus,
  ScenarioDetail,
  ScenarioReference,
  UpdateIssueReportStatusRequest,
  UpdateScenarioRequest,
  RecruitRemainingSummary,
  VaRequestRecord,
} from '@aegislab/shared';

/**
 * 상태 전이 PATCH 의 값 축 — 어려움 보고 취소·해결과 이탈 시그널 취소·해결이 같은 두 값을 쓴다.
 *
 * 사본이 아니라 shared 요청 스키마에서 **파생**시킨다: 두 스키마가 같은 enum 을 싣고 있어
 * 어느 한쪽을 골라 도출해도 값이 갈리지 않고, 여기 리터럴을 다시 적으면 그 순간 사본이 된다.
 */
export type OperationalSignalStatusTransition = UpdateIssueReportStatusRequest['status'];

export type { ScenarioDetail };

/**
 * 설문폼 만들기 한도. 서버는 토큰·복사·제목·편집자 수만큼의 권한·서비스계정 권한·발행·다시
 * 읽기·감시 등록을 차례로 부르고, 호출 하나마다 20초(`google-form-provision.service.ts`)만 걸
 * 뿐 전체 상한이 없다. 그래서 주간보고서 초안(#1673)처럼 "서버 상한보다 길게" 잡을 수 없고,
 * 느린 호출 몇 개를 견딜 만큼 잡는다. 그래도 끊기면 훅이 캠페인 칸을 다시 보고 판정한다.
 */
const PROVISION_TIMEOUT_MS = 60_000;

export const scenarioApi = {
  // ── Queries ─────────────────────────────────

  fetchReference: async (id: string): Promise<ScenarioReference> => {
    const { data } = await apiClient.get<ScenarioReference>(`/scenarios/${id}/reference`);
    return data;
  },

  fetchScenarios: async (teamId?: string): Promise<Scenario[]> => {
    const params = teamId ? { teamId } : {};
    const { data } = await apiClient.get<Scenario[]>('/scenarios', { params });
    return data;
  },

  fetchDetail: async (id: string): Promise<ScenarioDetail> => {
    const { data } = await apiClient.get<ScenarioDetail>(`/scenarios/${id}`);
    return data;
  },

  /**
   * 계약 목표 대비 확보·잔여 요약. 만들 수 없으면 null(섭외 CP 미태깅·계약 목표 0).
   *
   * `fetchDetail` 로 대신하지 말 것 — 그쪽은 타임라인·운영시그널까지 끌어오고 서버가
   * 응답을 만들며 섭외 앵커를 **기록**한다. 체크인 카드를 펼칠 때마다 탈 경로가 아니다.
   *
   * ⚠ NestJS 는 null 을 빈 본문으로 직렬화하고 axios 는 그걸 `''` 로 준다 — `?? null` 로 좁힌다.
   */
  fetchRecruitRemaining: async (id: string): Promise<RecruitRemainingSummary | null> => {
    const { data } = await apiClient.get<RecruitRemainingSummary | null>(
      `/scenarios/${id}/recruit-remaining`
    );
    return data || null;
  },

  /**
   * 본인 작성 중 초안 1건 (없으면 null). 임시저장 복원 진입점.
   * 초안은 목록(`GET /scenarios`)에서 제외되므로 이 경로가 유일하다.
   */
  fetchMyDraft: async (): Promise<Scenario | null> => {
    const { data } = await apiClient.get<Scenario | null>('/scenarios/my-draft');
    // ⚠ NestJS 는 핸들러가 null 을 돌려주면 **빈 본문**으로 직렬화한다. axios 는 그걸
    // null 이 아니라 빈 문자열('')로 준다 — `data != null` 가드를 그냥 통과해
    // 소비처에서 `data.title` 이 undefined 가 된다(실측: 시나리오 창 전체가 죽었다).
    // 경계에서 정규화한다.
    return data != null && typeof data === 'object' ? data : null;
  },

  // ── CRUD ────────────────────────────────────

  create: async (params: CreateScenarioRequest): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>('/scenarios', params);
    return data;
  },

  update: async (id: string, params: UpdateScenarioRequest): Promise<Scenario> => {
    const { data } = await apiClient.patch<Scenario>(`/scenarios/${id}`, params);
    return data;
  },

  /**
   * 내 초안 버리기. `remove` 와 달리 id 를 안 받는다 — 대상이 "내 초안"이라 서버가 작성자와
   * DRAFT 상태로 스스로 좁힌다. 초안이 없으면 no-op(204).
   *
   * `remove`(`scenario:delete`)는 ADMIN 전용이라 초안 작성자가 자기 초안을 못 지웠다.
   */
  removeMyDraft: async (): Promise<void> => {
    await apiClient.delete('/scenarios/my-draft');
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/scenarios/${id}`);
  },

  // ── Status transitions ─────────────────────

  /** 보관 전용 (COMPLETED/FAILED → ARCHIVED) — delete 권한 없이 팀장에게 개방된 경로. */
  archive: async (id: string): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>(`/scenarios/${id}/archive`);
    return data;
  },

  activate: async (id: string): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>(`/scenarios/${id}/activate`);
    return data;
  },

  complete: async (id: string): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>(`/scenarios/${id}/complete`);
    return data;
  },

  fail: async (id: string): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>(`/scenarios/${id}/fail`);
    return data;
  },

  // ── ADCODE 신청 (L4/L7) ─────────────────────
  // 전사 직원(인증만)이 본인이 읽을 수 있는 시나리오에서 애드코드를 신청.
  // ADCODE 보유 팀에 '시나리오 없는 ACTIVE 무보상' 퀘스트가 자동 생성되고 출처가 박제된다.
  // 응답: 생성된 Quest (origin='ADCODE_REQUEST'). 신청자는 보통 수행팀이 아니라 결과를 직접 보진 않음.
  requestAdcode: async (
    id: string,
    sheetUrl: string,
    description: string | null = null
  ): Promise<Quest> => {
    const { data } = await apiClient.post<Quest>(`/scenarios/${id}/adcode-request`, {
      sheetUrl,
      description,
    });
    return data;
  },

  // ── VA 업무지시 요청 (P2) ────────────────────
  // 캠페인 담당자(인증만)가 본인이 읽을 수 있는 시나리오에서 작업 제목·내용을 작성해 요청.
  // VA 보유 팀에 '시나리오 없는 ACTIVE 무보상' 퀘스트가 자동 생성되고 출처가 박제된다.
  // 응답: 생성된 Quest (origin='VA_REQUEST'). requestAdcode 미러 + 작업 제목(title) 필수.
  requestVa: async (
    id: string,
    title: string,
    sheetUrl: string | null = null,
    description: string | null = null
  ): Promise<Quest> => {
    const { data } = await apiClient.post<Quest>(`/scenarios/${id}/va-request`, {
      title,
      sheetUrl,
      description,
    });
    return data;
  },

  // ── ADCODE 신청 기록 조회 (PR2 L2) ──────────
  // 이 시나리오의 애드코드 신청 기록(출처표기)을 조회. 전용 read 엔드포인트가 시나리오
  // read-scope(findDetail) 가드를 재사용 → 목록 게이팅 무손상으로 신청 팀도 자기 요청 조회.
  // 서버가 status IN(ACTIVE,COMPLETED) 강제 → 응답은 진행/완료 2버킷. proof 내용 미노출.
  fetchAdcodeRequests: async (id: string): Promise<AdcodeRequestRecord[]> => {
    const { data } = await apiClient.get<AdcodeRequestRecord[]>(`/scenarios/${id}/adcode-requests`);
    return data;
  },

  // ── VA 업무지시 요청 기록 조회 (P4b L3) ──────
  // 이 시나리오의 VA 업무지시 요청 기록(출처표기)을 조회. fetchAdcodeRequests 미러 —
  // 전용 read 엔드포인트가 시나리오 read-scope(findDetail) 가드를 재사용 → 요청 팀도 자기 요청 조회.
  // 서버가 status IN(ACTIVE,COMPLETED) 강제 → 응답은 진행/완료 2버킷. sheetUrl(참고 링크) 추가 노출.
  fetchVaRequests: async (id: string): Promise<VaRequestRecord[]> => {
    const { data } = await apiClient.get<VaRequestRecord[]>(`/scenarios/${id}/va-requests`);
    return data;
  },

  // ── ADCODE 증빙 수락/반려 (L3/L7) ───────────
  // 시나리오 참여팀(read-scope)이 수행자 증빙을 수락(=quest 완료) 또는 반려(재제출 가능)한다.
  // scenarioId 바인딩으로 백엔드가 교차 시나리오 접근을 차단 → 응답은 전이된 Quest.
  acceptAdcodeRequest: async (scenarioId: string, questId: string): Promise<Quest> => {
    const { data } = await apiClient.post<Quest>(
      `/scenarios/${scenarioId}/adcode-requests/${questId}/accept`
    );
    return data;
  },

  rejectAdcodeRequest: async (
    scenarioId: string,
    questId: string,
    comment?: string
  ): Promise<Quest> => {
    const { data } = await apiClient.post<Quest>(
      `/scenarios/${scenarioId}/adcode-requests/${questId}/reject`,
      comment !== undefined ? { comment } : {}
    );
    return data;
  },

  // ── ADCODE 반려 스레드 댓글 (CP2) ────────────
  // 반려 사유(ProofComment 루트)에 달리는 검토자 ↔ 제출자 왕복 댓글 1건 작성.
  // 라우트만 quest 축(`POST /quests/:id/adcode-comments`) — 수행자는 시나리오 read-scope 밖일 수
  // 있어 시나리오 경로에 매달 수 없다. 쓰기 게이트는 서버의 canCommentOnAdcodeRequest 가 SSOT.
  addAdcodeComment: async (questId: string, body: string): Promise<AdcodeRequestComment> => {
    const { data } = await apiClient.post<AdcodeRequestComment>(
      `/quests/${questId}/adcode-comments`,
      { body }
    );
    return data;
  },

  // ── Teams ───────────────────────────────────

  addTeam: async (scenarioId: string, teamId: string): Promise<void> => {
    await apiClient.post(`/scenarios/${scenarioId}/teams/${teamId}`);
  },

  removeTeam: async (scenarioId: string, teamId: string): Promise<void> => {
    await apiClient.delete(`/scenarios/${scenarioId}/teams/${teamId}`);
  },

  // ── Quest Tagging ───────────────────────────

  tagQuest: async (scenarioId: string, questId: string): Promise<void> => {
    await apiClient.post(`/scenarios/${scenarioId}/quests/${questId}`);
  },

  untagQuest: async (scenarioId: string, questId: string): Promise<void> => {
    await apiClient.delete(`/scenarios/${scenarioId}/quests/${questId}`);
  },

  reorderQuests: async (scenarioId: string, questIds: string[]): Promise<void> => {
    await apiClient.patch(`/scenarios/${scenarioId}/quests/reorder`, { questIds });
  },

  /**
   * 설문폼 응답 시트 링크 (퀘스트 caf4cac8). `null` 이면 지운다.
   *
   * `update` 와 갈라 두는 이유는 서버 게이트가 다르기 때문이다 — 이쪽은 `scenario:outreach_form_write`
   * (팀원 개방)이고 `update` 는 활성화·보관까지 딸린 넓은 권한이다. 한 함수로 합치면 화면이
   * 어느 권한을 요구하는지 호출부에서 안 보인다.
   */
  updateOutreachForm: async (scenarioId: string, url: string | null): Promise<Scenario> => {
    const { data } = await apiClient.patch<Scenario>(`/scenarios/${scenarioId}/outreach-form`, {
      url,
    });
    return data;
  },

  /**
   * 설문폼 만들기 (퀘스트 8042eca6 CP3). 몸통이 없다 — 무엇을 복사하고 누구를 편집자로 넣을지는
   * 그 캠페인이 이미 든 값(타입·책임자)에서 서버가 낸다.
   *
   * 게이트는 링크 쓰기와 같은 `scenario:outreach_form_write` 다.
   *
   * 한도를 따로 준다(`PROVISION_TIMEOUT_MS`) — 서버가 구글을 차례로 여러 번 불러 apiClient
   * 전역 10초를 넘기면, 서버는 폼을 다 만들었는데 브라우저가 먼저 끊어 화면만 실패가 된다.
   */
  provisionOutreachForm: async (scenarioId: string): Promise<Scenario> => {
    const { data } = await apiClient.post<Scenario>(
      `/scenarios/${scenarioId}/outreach-form/provision`,
      undefined,
      { timeout: PROVISION_TIMEOUT_MS }
    );
    return data;
  },

  /**
   * 붙어 있는 폼의 지금 상태 (퀘스트 8042eca6 CP4). 팝오버를 열 때 한 번 부른다 —
   * 계약 확인이 구글 왕복이라 시나리오 상세에 얹지 않았다.
   */
  outreachFormStatus: async (scenarioId: string): Promise<ScenarioOutreachFormStatus> => {
    const { data } = await apiClient.get<ScenarioOutreachFormStatus>(
      `/scenarios/${scenarioId}/outreach-form/status`
    );
    return data;
  },

  /**
   * 잠긴 폼의 편집 권한을 되돌린다 (CP2 §1). 추가 발송 전에 사람이 누르고, 다시 보내면
   * 그 발송이 또 잠근다. 캠페인 데이터는 안 바뀌므로 돌려받을 몸통이 없다.
   */
  unlockOutreachForm: async (scenarioId: string): Promise<void> => {
    await apiClient.post(`/scenarios/${scenarioId}/outreach-form/unlock`);
  },

  /**
   * 폼 주인이 두 주소를 편집자로 넣은 뒤 누른다 (fact #2839). 서버가 감시를 다시 걸고 폼을
   * 읽어 본 결과를 기록한 다음 상태를 돌려준다.
   */
  recheckOutreachForm: async (scenarioId: string): Promise<ScenarioOutreachFormStatus> => {
    const { data } = await apiClient.post<ScenarioOutreachFormStatus>(
      `/scenarios/${scenarioId}/outreach-form/recheck`
    );
    return data;
  },

  /** 덮개 배너용 감시 상태. 서버 기록만 읽어 구글 왕복이 없다. */
  outreachFormWatch: async (scenarioId: string): Promise<OutreachFormWatchView> => {
    const { data } = await apiClient.get<OutreachFormWatchView>(
      `/scenarios/${scenarioId}/outreach-form/watch`
    );
    return data;
  },

  // ── Operational Signal ──────────────────────

  createIssueReport: async (
    scenarioId: string,
    payload: CreateOperationalIssueReportRequest
  ): Promise<void> => {
    await apiClient.post(`/scenarios/${scenarioId}/operational-issue-reports`, payload);
  },

  updateIssueReportStatus: async (
    reportId: string,
    status: OperationalSignalStatusTransition
  ): Promise<void> => {
    await apiClient.patch(`/operational-issue-reports/${reportId}`, { status });
  },

  /** 보고 스레드 댓글 작성 (보고자 ↔ 팀장 1-level). 성공 시 detail 재조회로 스레드 갱신. */
  addReportComment: async (reportId: string, body: string): Promise<void> => {
    await apiClient.post(`/operational-issue-reports/${reportId}/comments`, { body });
  },

  createDropSignal: async (scenarioId: string, payload: CreateDropSignalRequest): Promise<void> => {
    await apiClient.post(`/scenarios/${scenarioId}/drop-signals`, payload);
  },

  updateDropSignalStatus: async (
    dropSignalId: string,
    status: OperationalSignalStatusTransition
  ): Promise<void> => {
    await apiClient.patch(`/drop-signals/${dropSignalId}`, { status });
  },
};
